import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { Col } from 'react-bootstrap';
import SettingSidebar from '../Setting/SettingSidebar';
const SidebarContext = createContext();
export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SettingsLayout");
  }
  return context;
};
const SettingsLayout = ({ children }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  // Handle responsive layout changes
  const handleResponsiveChange = useCallback(() => {
    if (isMobile) {
      setIsExpanded(false);
    } else {
      // Tablet and Desktop (md and above): always expand sidebar
      setIsExpanded(true);
    }
    // Force re-render by updating initialization state
    setIsInitialized(prev => !prev);
  }, [isMobile]);

  // Auto-collapse on mobile and handle viewport changes
  useEffect(() => {
    handleResponsiveChange();
  }, [handleResponsiveChange]);

  // Ensure sidebar is expanded on initial mount for desktop/tablet
  useEffect(() => {
    if (!isMobile) {
      setIsExpanded(true);
    }
  }, []);

  // Listen for window resize events to handle dynamic viewport changes
  useEffect(() => {
    let resizeTimeout;
    const handleResize = () => {
      // Clear previous timeout
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      // Small delay to ensure media queries have updated
      resizeTimeout = setTimeout(() => {
        handleResponsiveChange();
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    // Also listen for orientation changes on mobile
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, [handleResponsiveChange]);

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };
  return (
    <SidebarContext.Provider value={{ isExpanded, setIsExpanded, toggleSidebar, isInitialized }}>
      <Grid className="d-flex settings-layout-container" key={`layout-${isInitialized}-${isMobile}`}>
        {!isMobile && (
          <Col
            className={`h-100  ${isExpanded ? 'col-2' : 'col-1'}`}
            xl={isExpanded ? 2 : 1}
            lg={isExpanded ? 3 : 1}
            md={isExpanded ? 4 : 1}
            style={{
              transition: 'all 0.3s ease',
              minWidth: isExpanded ? 'min(312px, 100%)' : '65px',
              maxWidth: isExpanded ? 'min(360px, 100%)' : '65px',
              flexShrink: 0,
              position: 'relative',
              zIndex: 10
            }}
          >
            <SettingSidebar />
          </Col>
        )}
        <Col
          className={`addProductServiceSideLine ${
            isMobile ? "col-12" : isExpanded ? "col-10" : "col-11"
          }`}
          style={{
            borderLeft: isMobile ? "none" : "0.5px solid rgba(6, 49, 100, 30%)",
            paddingLeft: isMobile ? "12px" : "20px",
            paddingRight: isMobile ? "12px" : "0px",
            marginTop: isMobile ? "0" : "-30px",
            transition: "all 0.3s ease",
            flex: 1,
            minWidth: 0,
            position: 'relative',
            zIndex: 1,
            marginLeft: '0px',
            boxSizing: 'border-box'
          }}
          xl={isMobile ? 12 : isExpanded ? 10 : 11}
          lg={isMobile ? 12 : isExpanded ? 9 : 11}
          md={isMobile ? 12 : isExpanded ? 8 : 11}
        >
          {/* JOB-3350: render SettingSidebar inline on mobile so the
              hamburger / drawer is reachable on small screens. Matches
              origin/main behavior — without this, mobile users have no
              way to switch between settings pages.

              JOB-3374: pass mobileTrigger="inline" so the settings
              hamburger uses in-flow positioning instead of the default
              `position: fixed` at (20px, 20px). The fixed variant
              overlapped MainNav's global hamburger, making the global
              hamburger appear missing on every settings page. */}
          {isMobile && <SettingSidebar mobileTrigger="inline" />}
          {children}
        </Col>
      </Grid>
    </SidebarContext.Provider>
  );
};
export default SettingsLayout;